import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart3, X, Trophy } from "lucide-react";
import { SavedScenario } from "@/hooks/useSavedScenarios";
import { cn } from "@/lib/utils";

interface ScenarioComparisonViewProps {
  scenarios: SavedScenario[];
  selectedIds: string[];
  onClose: () => void;
  language: string;
  formatCurrency: (value: number) => string;
}

type MetricKey = 'totalYearly' | 'paybackMonths' | 'yearOneROI' | 'revenueLost' | 'costSavings';

const ScenarioComparisonView = ({
  scenarios,
  selectedIds,
  onClose,
  language,
  formatCurrency
}: ScenarioComparisonViewProps) => {
  const selectedScenarios = scenarios.filter(s => selectedIds.includes(s.id));

  const metrics: { key: MetricKey; label: string; suffix?: string }[] = [
    { key: 'totalYearly', label: language === 'sv' ? 'Årlig påverkan' : 'Annual Impact' },
    { key: 'paybackMonths', label: language === 'sv' ? 'Återbetalningstid' : 'Payback Period', suffix: language === 'sv' ? ' mån' : ' mo' },
    { key: 'yearOneROI', label: language === 'sv' ? 'År 1 ROI' : 'Year 1 ROI', suffix: '%' },
    { key: 'revenueLost', label: language === 'sv' ? 'Förlorade intäkter' : 'Revenue Lost' },
    { key: 'costSavings', label: language === 'sv' ? 'Kostnadsbesparingar' : 'Cost Savings' }
  ];

  // Find best value for each metric
  const getBest = (key: MetricKey) => {
    if (key === 'paybackMonths') {
      return Math.min(...selectedScenarios.map(s => s.results[key]));
    }
    return Math.max(...selectedScenarios.map(s => s.results[key]));
  };

  const formatValue = (key: MetricKey, value: number) => {
    if (key === 'totalYearly' || key === 'revenueLost' || key === 'costSavings') {
      return formatCurrency(value);
    }
    return value.toFixed(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-brand-500" />
          {language === 'sv' ? 'Jämförelse av scenarier' : 'Scenario Comparison'}
        </h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scenario headers */}
      <div className="overflow-x-auto">
        <div 
          className="grid gap-4 min-w-fit" 
          style={{ gridTemplateColumns: `140px repeat(${selectedScenarios.length}, minmax(100px, 1fr))` }}
        >
          <div /> {/* Empty corner cell */}
          {selectedScenarios.map((scenario) => (
            <div key={scenario.id} className="text-center">
              <div
                className="w-4 h-4 rounded-full mx-auto mb-2"
                style={{ backgroundColor: scenario.color }}
              />
              <p className="font-semibold text-sm truncate">{scenario.name}</p>
              <p className="text-xs text-muted-foreground">
                {scenario.inputs.numberOfProperties} {language === 'sv' ? 'fast.' : 'prop.'}
              </p>
            </div>
          ))}

          {/* Metric rows */}
          {metrics.map((metric) => (
            <React.Fragment key={metric.key}>
              <div className="flex items-center text-sm text-muted-foreground">
                {metric.label}
              </div>
              {selectedScenarios.map((scenario) => {
                const value = scenario.results[metric.key];
                const isBest = getBest(metric.key) === value && selectedScenarios.length > 1;

                return (
                  <motion.div
                    key={scenario.id}
                    className={cn(
                      "text-center p-2 rounded-lg",
                      isBest && "bg-green-50 dark:bg-green-900/20 ring-1 ring-green-500/30"
                    )}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <p className={cn(
                      "font-bold text-sm",
                      isBest && "text-green-600"
                    )}>
                      {formatValue(metric.key, value)}
                      {metric.suffix}
                    </p>
                    {isBest && (
                      <Badge className="text-[10px] bg-green-500 mt-1 px-1">
                        <Trophy className="h-2 w-2 mr-1" />
                        Best
                      </Badge>
                    )}
                  </motion.div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Visual bar comparison */}
      <Card className="p-4">
        <h4 className="text-sm font-medium mb-3">
          {language === 'sv' ? 'Årlig påverkan jämförelse' : 'Annual Impact Comparison'}
        </h4>
        <div className="space-y-3">
          {selectedScenarios.map((scenario, index) => {
            const maxValue = Math.max(...selectedScenarios.map(s => s.results.totalYearly));
            const percentage = (scenario.results.totalYearly / maxValue) * 100;

            return (
              <div key={scenario.id} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: scenario.color }}
                    />
                    <span className="truncate max-w-[150px]">{scenario.name}</span>
                  </span>
                  <span className="font-semibold">
                    {formatCurrency(scenario.results.totalYearly)}
                  </span>
                </div>
                <div className="h-4 rounded-full overflow-hidden bg-muted">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: scenario.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </motion.div>
  );
};

export default ScenarioComparisonView;
